import { captureException } from '@sentry/react';
import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models/neighborhood';
import { adaptV2ServiceAreas } from './adaptV2Neighborhood';
import {
    V2NeighborhoodRequest,
    V2NeighborhoodResponse,
    V2_NEIGHBORHOOD_MAX_LIMIT,
} from './v2NeighborhoodTypes';

/**
 * Upper bound on pages walked when collecting an account's full service-area
 * list. At the max limit this covers 2,000 areas — far beyond any real account —
 * and exists only so a service that kept returning `hasMore: true` could not spin
 * forever. Hitting it is reported and falls back to v1 rather than silently
 * serving a truncated map.
 */
const MAX_PAGES = 20;

/**
 * The v2 service reports errors as `{ error: { code, message } }` at HTTP 200
 * (its `fromContract` wrapper serializes the success and error shapes into one
 * 200 union). A plain `res.json()` would therefore read a NOT_FOUND as a success
 * whose `serviceAreas` is undefined, and the map would render empty with no
 * failure anywhere to see.
 */
type V2ErrorEnvelope = { error?: { code?: string; message?: string } };

type FetchV2NeighborhoodPageParams = {
    url: string;
    body: V2NeighborhoodRequest;
};

export const fetchV2NeighborhoodPage = async ({
    url,
    body,
}: FetchV2NeighborhoodPageParams): Promise<
    V2NeighborhoodResponse | undefined
> => {
    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            captureException(
                new Error(`V2 neighborhood fetch failed: HTTP ${res.status}`),
                { extra: { accountId: body.accountId, page: body.page } }
            );
            return undefined;
        }

        const parsed = (await res.json()) as V2NeighborhoodResponse &
            V2ErrorEnvelope;

        if (parsed.error) {
            captureException(
                new Error(
                    `V2 neighborhood fetch failed: ${parsed.error.code}: ${parsed.error.message}`
                ),
                { extra: { accountId: body.accountId, page: body.page } }
            );
            return undefined;
        }

        return parsed;
    } catch (e) {
        captureException(e, {
            extra: { accountId: body.accountId, page: body.page },
        });
        return undefined;
    }
};

type FetchAllV2ServiceAreasParams = {
    url: string;
    accountId: string;
    /**
     * Page 1 prefetched by the plugin, if it supplied one. Reusing it avoids
     * repeating a request the host page already paid for.
     *
     * Only used when it is a *complete* list (`hasMore: false`) — see the body
     * for why continuing from a partial prefetch is unsafe.
     */
    prefetched?: V2NeighborhoodResponse | undefined;
};

/**
 * Collect every service area for an account.
 *
 * v1's `/serviceareas/list` returned all areas in a single unpaginated response;
 * v2 caps `limit` at 100, so parity requires walking pages. Returns undefined
 * whenever the walk cannot be completed — a failed page, or the MAX_PAGES
 * ceiling — so the caller can fall back to v1 rather than render a
 * partially-loaded map as if it were complete. A complete walk of zero areas is
 * an empty array, not undefined: that is an answer, not a failure.
 *
 * Sends no postcode, which is how the route expresses "all areas".
 */
export const fetchAllV2ServiceAreas = async ({
    url,
    accountId,
    prefetched,
}: FetchAllV2ServiceAreasParams): Promise<
    NeighborhoodServiceAreaInfo[] | undefined
> => {
    const pages: V2NeighborhoodResponse[] = [];

    // Only take the prefetch when it is the whole list. Continuing from page 2
    // would assume the plugin fetched page 1 at *this* module's limit, but the
    // plugin declares its own constant in its own package — nothing makes the two
    // agree, and a drift would silently skip or duplicate the areas in between
    // (plugin limit 50 + widget limit 100 loses areas 51-100 entirely). A
    // `hasMore: false` prefetch is complete whatever limit produced it, so it is
    // safe to use and needs no cross-package agreement.
    //
    // The cost is discarding a prefetch for accounts that exceed the plugin's
    // limit, which then pay one extra request. Accounts that large are rare, and
    // the alternative is a silent-data-loss coupling between two packages.
    let firstPage = prefetched?.hasMore ? undefined : prefetched;
    if (!firstPage) {
        firstPage = await fetchV2NeighborhoodPage({
            url,
            body: {
                accountId,
                page: 1,
                limit: V2_NEIGHBORHOOD_MAX_LIMIT,
            },
        });
        if (!firstPage) {
            return undefined;
        }
    }
    pages.push(firstPage);

    let page = 2;
    while (pages[pages.length - 1]?.hasMore && page <= MAX_PAGES) {
        const next = await fetchV2NeighborhoodPage({
            url,
            body: {
                accountId,
                page,
                limit: V2_NEIGHBORHOOD_MAX_LIMIT,
            },
        });
        if (!next) {
            return undefined;
        }
        pages.push(next);
        page += 1;
    }

    if (pages[pages.length - 1]?.hasMore) {
        // Report and fall back, rather than truncate quietly: a short list looks
        // exactly like a correct one, and the map would just be missing areas.
        //
        // Returning undefined here matches every other incomplete-walk branch, so
        // the caller renders v1's complete list instead. The ceiling is 2,000
        // areas (MAX_PAGES x V2_NEIGHBORHOOD_MAX_LIMIT), which no real account
        // approaches, so reaching this means the service kept reporting hasMore
        // — and a v2 list cut off at an arbitrary page boundary is worth less
        // than one more request for the whole thing.
        captureException(
            new Error(
                `V2 service-area list exceeded ${MAX_PAGES} pages; falling back to v1`
            ),
            { extra: { accountId } }
        );
        return undefined;
    }

    const areas = adaptV2ServiceAreas(
        pages.flatMap((p) => p.serviceAreas ?? [])
    );

    // Reproduce v1's ordering. `getServiceAreasForAccount` sorted by `jobs.count`
    // descending; the v2 handler does an unsorted find(), so without this the
    // areas arrive in natural collection order.
    //
    // This is not cosmetic: clusterServiceAreas is a greedy sequential pass that
    // assigns each area to the first cluster within the zoom threshold and
    // recomputes that cluster's centroid as it goes, so the input order decides
    // which zips group together and where cluster pins land.
    // (filterServiceAreas is order-independent — it picks the max by reduce — so
    // only clustering is affected.)
    //
    // The adapted `jobCount` is now the all-status count (`jobCountTotal`, from
    // RFC-NEIGHBORHOOD-MAP-DENSITY) — the same value v1's `jobs.count` carried —
    // so this reproduces v1's ranking rather than approximating it. For a response
    // that predates the density work the adapter falls back to the published-only
    // count, which still sorts, just not identically to v1.
    return areas.sort((a, b) => b.jobCount - a.jobCount);
};
